import React from 'react';
import { Truck, MapPin, Phone, User, RefreshCw } from 'lucide-react';
import { useTelegram } from '../hooks/useTelegram';

const getApiPath = (path) => {
  if (!path || typeof path !== 'string') return path;
  if (!path.startsWith('/api/')) return path;
  const apiBase = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
  return apiBase ? `${apiBase}${path}` : path;
};

const apiPost = async (path, body) => {
  const res = await fetch(getApiPath(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const message = data?.error || `Request failed: ${res.status}`;
    throw new Error(message);
  }
  return data;
};

const PAGE_SIZE = 10;
const STATUSES = ['В обработке', 'Ожидает', 'Задерживается', 'Доставлено'];

const statusColors = {
  'В обработке': 'bg-yellow-500/15 text-yellow-300 border-yellow-500/35',
  'Ожидает': 'bg-yellow-500/15 text-yellow-300 border-yellow-500/35',
  'Задерживается': 'bg-orange-500/15 text-orange-300 border-orange-500/35',
  'Доставлено': 'bg-green-500/15 text-green-300 border-green-500/35',
  'Отменена': 'bg-red-500/15 text-red-300 border-red-500/35',
};

const statusIcons = {
  'В обработке': '📦',
  'Ожидает': '📦',
  'Задерживается': '⏳',
  'Доставлено': '✅',
  'Отменена': '❌',
};

const CourierPage = () => {
  const { initData, tg } = useTelegram();
  const [deliveries, setDeliveries] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [error, setError] = React.useState('');
  const [updatingId, setUpdatingId] = React.useState(null);
  const [tab, setTab] = React.useState('active');

  const loadDeliveries = React.useCallback(async (nextPage = 0, append = false) => {
    const initDataRaw = initData?.initData;
    if (!initDataRaw) return;
    setError('');
    if (append) setLoadingMore(true);
    else setLoading(true);
    try {
      const r = await apiPost('/api/courier/delivery/list', {
        initData: initDataRaw,
        tab,
        page: nextPage,
        limit: PAGE_SIZE,
      });
      const items = Array.isArray(r?.deliveries) ? r.deliveries : [];
      if (append) {
        setDeliveries((prev) => [...prev, ...items]);
      } else {
        setDeliveries(items);
      }
      setTotal(r?.total || 0);
      setPage(nextPage);
    } catch (e) {
      setError(e?.message || 'Ошибка загрузки');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [initData?.initData, tab]);

  React.useEffect(() => {
    setDeliveries([]);
    setPage(0);
    setTotal(0);
    loadDeliveries(0, false);
  }, [loadDeliveries]);

  const handleLoadMore = () => {
    loadDeliveries(page + 1, true);
  };

  const hasMore = deliveries.length < total;

  const confirmAction = (message) =>
    new Promise((resolve) => {
      if (tg?.showPopup) {
        tg.showPopup(
          {
            title: 'Подтверждение',
            message: String(message || ''),
            buttons: [
              { id: 'cancel', type: 'cancel', text: 'Отмена' },
              { id: 'ok', type: 'default', text: 'Да' },
            ],
          },
          (buttonId) => resolve(buttonId === 'ok')
        );
        return;
      }
      resolve(window.confirm(String(message || '')));
    });

  const handleStatusChange = async (deliveryId, newStatus) => {
    const ok = await confirmAction(`Изменить статус на "${newStatus}"?`);
    if (!ok) return;

    const initDataRaw = initData?.initData;
    if (!initDataRaw) return;

    setUpdatingId(deliveryId);
    try {
      await apiPost('/api/courier/delivery/status', {
        initData: initDataRaw,
        deliveryId,
        newStatus,
      });
      // Перезагрузить с начала после смены статуса
      loadDeliveries(0, false);
    } catch (e) {
      setError(e?.message || 'Ошибка обновления');
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-bg text-text-primary pb-20">
      <div className="px-4 pt-6 max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-2xl border border-stroke bg-gradient-to-br from-white/10 to-white/5 flex items-center justify-center">
              <Truck size={22} className="text-neon-yellow" />
            </div>
            <div>
              <h1 className="text-xl font-semibold tracking-tight">Курьер</h1>
              <p className="text-xs text-text-secondary">Доставки</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => loadDeliveries(0, false)}
            disabled={loading}
            className="h-9 px-4 rounded-2xl font-semibold text-xs border border-stroke bg-white/5 text-text-primary hover:bg-white/8 transition disabled:opacity-60"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          <button
            type="button"
            onClick={() => setTab('active')}
            className={`flex-1 h-9 rounded-xl font-semibold text-xs border transition ${tab === 'active' ? 'bg-gradient-to-r from-[#3B82F6] to-[#8A5CF6] text-white border-transparent' : 'border-stroke bg-white/5 text-text-secondary hover:bg-white/8'}`}
          >
            Активные
          </button>
          <button
            type="button"
            onClick={() => setTab('completed')}
            className={`flex-1 h-9 rounded-xl font-semibold text-xs border transition ${tab === 'completed' ? 'bg-gradient-to-r from-[#3B82F6] to-[#8A5CF6] text-white border-transparent' : 'border-stroke bg-white/5 text-text-secondary hover:bg-white/8'}`}
          >
            Завершённые
          </button>
        </div>

        {error ? <div className="mb-4 text-xs text-red-300">{error}</div> : null}

        {loading && !deliveries.length ? (
          <div className="text-center text-sm text-text-secondary py-10">Загрузка…</div>
        ) : null}

        {!loading && !deliveries.length ? (
          <div className="rounded-2xl border border-stroke bg-surface backdrop-blur-md p-5 text-center">
            <div className="text-sm text-text-secondary">
              {tab === 'active' ? 'Нет активных доставок' : 'Нет завершённых доставок'}
            </div>
          </div>
        ) : null}

        {deliveries.length > 0 ? (
          <div className="text-xs text-text-muted mb-3">Показано: {deliveries.length} из {total}</div>
        ) : null}

        <div className="space-y-4">
          {deliveries.map((d) => {
            const isUpdating = updatingId === d.id;
            const currentIdx = STATUSES.indexOf(d.status);

            return (
              <div key={d.id} className="rounded-2xl border border-stroke bg-surface backdrop-blur-md p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-lg border ${statusColors[d.status] || ''}`}>
                    {statusIcons[d.status] || ''} {d.status}
                  </span>
                  <span className="text-[11px] text-text-muted">#{d.id}</span>
                </div>

                <div className="text-xs text-text-secondary space-y-1.5 mb-4">
                  <div className="flex items-start gap-2">
                    <User size={13} className="text-text-muted shrink-0 mt-0.5" />
                    <span className="text-text-primary font-medium">{d.full_name || '—'}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <MapPin size={13} className="text-text-muted shrink-0 mt-0.5" />
                    <span>{d.address || '—'}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-text-muted text-xs">Регион:</span>
                    <span className="text-xs font-medium">{d.region || '—'}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Phone size={13} className="text-text-muted shrink-0 mt-0.5" />
                    <span>{d.phone_digits ? `+${d.phone_digits}` : '—'}</span>
                  </div>
                  <div className="text-[11px] text-text-muted">
                    Дата доставки: {d.delivery_date || '—'} · Создано: {d.created_at || ''}
                  </div>
                </div>

                {tab === 'active' ? (
                  <div className="flex flex-wrap gap-2">
                    {STATUSES.filter((s) => s !== d.status).map((s) => {
                      const sIdx = STATUSES.indexOf(s);
                      const isPrimary = sIdx === currentIdx + 1;
                      return (
                        <button
                          key={s}
                          type="button"
                          disabled={isUpdating}
                          onClick={() => handleStatusChange(d.id, s)}
                          className={`h-8 px-3 rounded-xl font-semibold text-[11px] border transition disabled:opacity-60 ${
                            isPrimary
                              ? 'bg-gradient-to-r from-[#3B82F6] to-[#8A5CF6] text-white border-transparent'
                              : 'border-stroke bg-white/5 text-text-secondary hover:bg-white/8'
                          }`}
                        >
                          {statusIcons[s]} {s}
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>

        {hasMore ? (
          <button
            type="button"
            onClick={handleLoadMore}
            disabled={loadingMore}
            className="mt-4 w-full py-3 rounded-2xl font-semibold text-sm border border-stroke bg-white/5 text-text-primary hover:bg-white/8 transition disabled:opacity-60"
          >
            {loadingMore ? 'Загрузка…' : 'Показать ещё'}
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default CourierPage;

